import { Hooks } from '@3nickels/data-modules';
import { useCallback } from 'react';
import { useModalContentView } from '../pages/layout/ContentViewContext';

const FIND_YOUR_INSTITUTION_MODAL_KEY = 'find-your-institution';

function deferUntilAfterPaint(callback: () => void) {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      callback();
    });
    return;
  }

  setTimeout(callback, 0);
}

export type FindYourInstitutionManualTarget =
  | 'cash-account'
  | 'retirement-savings-plan'
  | 'pension-plan'
  | 'fixed-annuity'
  | 'car'
  | 'home'
  | 'investment-real-estate'
  | 'other-asset'
  | 'brokerage-account'
  | 'college-savings-plan'
  | 'health-savings-account'
  | 'business'
  | 'debt-credit-card'
  | 'debt-medical'
  | 'debt-personal-loan'
  | 'debt-student-loan'
  | 'debt-other'
  | 'mortgage-type';

export type FindYourInstitutionNavigationHandlers = {
  onAutoLinkSuccess?: () => Promise<void> | void;
  onContinueLinking?: (accessId: number) => Promise<void> | void;
  onManualNavigation?: (
    target: FindYourInstitutionManualTarget,
    subtype?: string
  ) => Promise<void> | void;
};

export type FindYourInstitutionModalOptions = FindYourInstitutionNavigationHandlers & {
  resetWizard?: boolean;
  hideContinueLinkingSection?: boolean;
  hideManualAddButton?: boolean;
};

export function useFindYourInstitutionModal() {
  const { openModal, closeModal, getModalData } = useModalContentView();
  const wizard = Hooks.usePlaidWizard();

  const openFindYourInstitutionModal = useCallback(
    (options: boolean | FindYourInstitutionModalOptions = false) => {
      const resolvedOptions = typeof options === 'boolean' ? { resetWizard: options } : options;

      openModal(FIND_YOUR_INSTITUTION_MODAL_KEY, resolvedOptions);

      if (resolvedOptions.resetWizard) {
        deferUntilAfterPaint(() => {
          wizard.reset();
        });
      }
    },
    [openModal, wizard]
  );

  const closeFindYourInstitutionModal = useCallback(() => {
    closeModal(FIND_YOUR_INSTITUTION_MODAL_KEY);
  }, [closeModal]);

  return {
    closeFindYourInstitutionModal,
    modalKey: FIND_YOUR_INSTITUTION_MODAL_KEY,
    modalOptions: getModalData<FindYourInstitutionModalOptions>(FIND_YOUR_INSTITUTION_MODAL_KEY),
    openFindYourInstitutionModal,
  };
}
